import React from "react";
import { useTranslation } from "react-i18next";
import BrandLogo from "./BrandLogo";
import ImageCarousel from "./ImageCarousel";
import { useTheme } from "../context/ThemeContext";
import { ShoppingCart, Heart, Plus, Minus, Check } from "lucide-react";

const SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "$",
  CAD: "$",
  JPY: "¥",
  CNY: "¥",
  KRW: "₩",
  INR: "₹",
  CHF: "Fr.",
  SEK: "kr",
  DKK: "kr",
  NOK: "kr",
  BRL: "R$",
  MXN: "$",
  MYR: "RM",
  AED: "د.إ",
  SAR: "SR",
};

function d(hit, key, fallback = null) {
  return hit?.data?.[key] ?? hit?.[key] ?? fallback;
}

export default function ConverterCardLite({
  hit,
  computedPrice,
  currencyCode,
  showPrice = true,

  // New optional props for actions
  isFavorited = false,
  onFavoriteClick, // (event) => void
  onAddToCart, // (event, qty) => void
  favoriteDisabled = false,
  onImageClick,
}) {
  const { theme: t, isForest } = useTheme();
  const { t: tr } = useTranslation();
  const code = d(hit, "code", hit.objectID);
  const brand = d(hit, "brand");
  const img = d(hit, "imgURL");
  const country = d(hit, "country");
  const price =
    typeof computedPrice === "number"
      ? computedPrice
      : d(hit, `price${String(currencyCode || "USD").toUpperCase()}`);

  const symbol = SYMBOLS[currencyCode] || "";
  const currencyLabel = !symbol ? currencyCode : "";

  // Local UI state: quantity controller
  const [qty, setQty] = React.useState(1);
  const [qtyText, setQtyText] = React.useState("1");
  const [adding, setAdding] = React.useState(false);
  const [added, setAdded] = React.useState(false);

  // Commit text field to numeric qty
  const commitQty = () => {
    const text = (qtyText || "").trim();
    if (text === "") {
      setQtyText(String(qty));
      return;
    }
    const normalized = text.replace(",", ".");
    const parsed = parseFloat(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setQtyText(String(qty));
      return;
    }
    setQty(parsed);
    setQtyText(String(parsed));
  };

  // +/- whole steps
  const adjustQty = (delta) => {
    const normalized = String(qtyText ?? qty).replace(",", ".");
    const base = Number.isFinite(parseFloat(normalized))
      ? parseFloat(normalized)
      : qty;
    const next = Math.max(0, base + delta);
    setQty(next);
    setQtyText(String(next));
  };

  // Input handler for quantity (allows decimals)
  const handleQtyInput = (e) => {
    const val = e.target.value;
    if (/^[0-9]*([.,][0-9]*)?$/.test(val) || val === '') {
      setQtyText(val);
    }
  };

  const handleQtyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setQtyText(String(qty));
      e.currentTarget.blur();
    }
    if (/^[a-zA-Z]$/.test(e.key)) e.preventDefault();
    if (e.key === ',') {
      e.preventDefault();
      const target = e.currentTarget;
      const { selectionStart, selectionEnd, value } = target;
      const next = value.slice(0, selectionStart) + '.' + value.slice(selectionEnd);
      target.value = next;
      const ev = new Event('input', { bubbles: true });
      target.dispatchEvent(ev);
      setTimeout(() => target.setSelectionRange(selectionStart + 1, selectionStart + 1), 0);
    }
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (adding || qty <= 0) return;
    try {
      setAdding(true);
      await onAddToCart?.(e, qty);
      setAdded(true);
      setTimeout(() => setAdded(false), 900);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="h-full w-full rounded-2xl shadow-md p-0.5 md:p-5 flex flex-col justify-between gap-1 md:gap-3 transition-all duration-150 hover:-translate-y-1"
      style={{
        backgroundColor: t.surface.card,
        boxShadow: `0 0 0 1px ${t.surface.cardBorder}, 0 4px 6px -1px ${t.surface.cardShadow}`,
      }}
    >
      {/* Image block with header */}
      <div
        className="rounded-xl overflow-hidden relative pb-6 md:pb-8"
        style={{
          backgroundColor: isForest ? '#ffffff' : '#ffffff',
          boxShadow: `0 0 0 1px ${isForest ? 'rgba(226,232,240,0.6)' : 'rgba(203,213,225,0.8)'}`,
        }}
      >
        {/* Header row - compact on mobile like app */}
        <div className="flex items-center justify-between gap-1 md:gap-3 px-2.5 pt-2 pb-1 md:p-3 md:min-h-[84px]">
          <div className="flex flex-nowrap items-center gap-1 md:gap-2 flex-1 min-w-0 pr-1 md:pr-2 overflow-hidden">
            {brand &&
              brand.split("/").slice(0, 2).map((b) => (
                <span
                  key={b}
                  className="text-[11px] md:text-sm font-semibold px-2 md:px-2.5 py-1 md:py-1.5 rounded-md truncate"
                  style={{
                    backgroundColor: '#e8f5e9',
                    color: '#1a4d40',
                    boxShadow: '0 0 0 1px #a5d6a7',
                  }}
                >
                  {b}
                </span>
              ))}
            {country && (
              <span
                className="text-[11px] md:text-sm px-2 md:px-2.5 py-1 md:py-1.5 rounded-md truncate"
                style={{
                  backgroundColor: '#e8f5e9',
                  color: '#1a4d40',
                  boxShadow: '0 0 0 1px #a5d6a7',
                }}
              >
                {country}
              </span>
            )}
          </div>
          <div className="shrink-0 w-[72px] md:w-[160px] h-[36px] md:h-[72px] flex items-center justify-end pr-1 overflow-hidden">
            <BrandLogo
              label={brand}
              variant="noText"
              mode="plain"
              className="h-full max-h-full max-w-full"
            />
          </div>
        </div>

        {/* Image carousel - 140px on mobile like app */}
        <div
          className="flex items-center justify-center h-[140px] md:h-[220px] overflow-visible cursor-pointer"
          onClick={(e) => { e.stopPropagation(); onImageClick?.(); }}
        >
          <ImageCarousel
            sources={img}
            alt={code}
            className="w-full h-full"
            imageClassName="object-contain"
            paginationId={`pag-${String(code || '').replace(/[^a-zA-Z0-9_-]/g, '')}`}
          />
        </div>
        <div
          className="absolute inset-x-0 bottom-0 h-8 flex items-center justify-center ecat-swiper"
          onClick={(e) => e.stopPropagation()}
        >
          <div id={`pag-${String(code || '').replace(/[^a-zA-Z0-9_-]/g, '')}`} className="swiper-pagination" />
        </div>
      </div>

      {/* Footer: code + price - compact on mobile like app */}
      <div className="flex items-start justify-between px-2.5 md:px-0 min-h-[40px] md:min-h-[72px]">
        <div className="flex flex-col flex-1 mr-2">
          <div className="text-[11px] md:text-base uppercase tracking-wide" style={{ color: t.text.tertiary }}>
            {tr('converters.code')}
          </div>
          <div className="font-semibold text-base md:text-xl leading-tight line-clamp-2" style={{ color: t.text.primary }}>
            {code}
          </div>
        </div>

        {showPrice && typeof price === "number" && (
          <div className="text-right min-w-[70px]">
            <div className="text-[11px] md:text-base uppercase" style={{ color: t.text.tertiary }}>{tr('converters.price')}</div>
            <div className="font-bold text-base md:text-xl" style={{ color: t.price.text }}>
              {symbol && <span>{symbol} </span>}
              {price.toFixed(2)}{" "}
              {currencyLabel && (
                <span className="text-xs md:text-sm" style={{ color: t.text.secondary }}>{currencyLabel}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions: Elegant inline design */}
      <div className="px-2.5 md:px-0 flex flex-col gap-2 md:gap-3">
        {/* Desktop: Single row with qty + add button + favorite */}
        <div className="hidden md:flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {/* Quantity controls - compact inline */}
          <div
            className="flex items-center rounded-lg overflow-hidden"
            style={{
              backgroundColor: t.input.background,
              border: `1px solid ${t.input.border}`,
            }}
          >
            <button
              type="button"
              onClick={() => adjustQty(-1)}
              className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-80"
              style={{ color: t.text.secondary }}
            >
              <Minus size={16} />
            </button>
            <input
              type="text"
              inputMode="decimal"
              className="w-14 h-9 text-center bg-transparent border-none outline-none font-medium"
              style={{ color: t.text.primary }}
              value={qtyText}
              onChange={handleQtyInput}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={commitQty}
              onKeyDown={handleQtyKeyDown}
              onWheel={(e) => e.currentTarget.blur()}
            />
            <button
              type="button"
              onClick={() => adjustQty(1)}
              className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-80"
              style={{ color: t.text.secondary }}
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Add to Buy List button */}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={adding || qty <= 0}
            className="flex-1 h-9 inline-flex items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-all"
            style={{
              backgroundColor: added ? t.interactive.success : (qty > 0 ? t.interactive.primary : t.cardControls.buttonBackground),
              color: added || qty > 0 ? '#ffffff' : t.text.tertiary,
              opacity: adding ? 0.7 : 1,
              cursor: adding ? 'wait' : (qty <= 0 ? 'not-allowed' : 'pointer'),
            }}
          >
            {added ? <Check size={16} /> : <ShoppingCart size={16} />}
            <span>{added ? tr('converters.added') : adding ? tr('converters.adding') : tr('converters.addToList')}</span>
          </button>

          {/* Favorite button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFavoriteClick?.(e); }}
            disabled={favoriteDisabled}
            className="w-9 h-9 flex items-center justify-center rounded-lg transition-all"
            style={{
              backgroundColor: '#ffffff',
              color: isFavorited ? t.interactive.primary : t.cardControls.favoriteIcon,
              border: `1px solid ${isForest ? 'rgba(203,213,225,0.3)' : 'rgba(203,213,225,0.8)'}`,
              opacity: favoriteDisabled ? 0.5 : 1,
              cursor: favoriteDisabled ? 'not-allowed' : 'pointer',
            }}
            aria-pressed={isFavorited}
            aria-label={isFavorited ? tr('navigation.favorites') : tr('converters.addToList')}
          >
            {favoriteDisabled ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Heart size={18} fill={isFavorited ? "currentColor" : "none"} strokeWidth={2} />
            )}
          </button>
        </div>

        {/* Mobile: App-like compact design */}
        <div className="flex md:hidden flex-col gap-2" onClick={(e) => e.stopPropagation()}>
          {/* Quantity label + row */}
          <div>
            <div className="text-[11px] uppercase mb-0.5" style={{ color: t.text.tertiary }}>{tr('converters.quantity')}</div>
            <div
              className="flex items-center gap-1.5"
            >
              <button
                type="button"
                onClick={() => adjustQty(-1)}
                className="px-2 py-1.5 rounded-md flex items-center justify-center"
                style={{ backgroundColor: t.cardControls?.buttonBackground || t.input.background }}
              >
                <Minus size={14} style={{ color: t.text.primary }} />
              </button>
              <input
                type="text"
                inputMode="decimal"
                className="flex-1 h-8 text-center bg-transparent border rounded-lg outline-none font-bold text-[13px]"
                style={{
                  color: t.text.primary,
                  backgroundColor: t.input.background,
                  borderColor: t.input.border,
                }}
                value={qtyText}
                onChange={handleQtyInput}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={commitQty}
                onKeyDown={handleQtyKeyDown}
                onWheel={(e) => e.currentTarget.blur()}
              />
              <button
                type="button"
                onClick={() => adjustQty(1)}
                className="px-2 py-1.5 rounded-md flex items-center justify-center"
                style={{ backgroundColor: t.cardControls?.buttonBackground || t.input.background }}
              >
                <Plus size={14} style={{ color: t.text.primary }} />
              </button>
            </div>
          </div>

          {/* Action buttons row - app-like */}
          <div className="flex items-stretch gap-2">
            {/* Add to Buy List */}
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={adding || qty <= 0}
              className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-xl text-[13px] font-bold transition-all"
              style={{
                backgroundColor: added ? t.interactive.success : (qty > 0 ? t.interactive.primary : t.cardControls.buttonBackground),
                color: added || qty > 0 ? '#ffffff' : t.text.tertiary,
                opacity: adding ? 0.7 : 1,
                cursor: adding ? 'wait' : (qty <= 0 ? 'not-allowed' : 'pointer'),
              }}
            >
              <span>{added ? tr('converters.added') : adding ? tr('converters.adding') : tr('converters.addToBuyList')}</span>
            </button>

            {/* Favorite */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onFavoriteClick?.(e); }}
              disabled={favoriteDisabled}
              className="w-10 h-10 flex items-center justify-center rounded-xl transition-all"
              style={{
                backgroundColor: '#ffffff',
                color: isFavorited ? t.interactive.primary : t.cardControls.favoriteIcon,
                border: `1px solid ${isForest ? 'rgba(203,213,225,0.3)' : 'rgba(148,163,184,0.6)'}`,
                opacity: favoriteDisabled ? 0.5 : 1,
                cursor: favoriteDisabled ? 'not-allowed' : 'pointer',
              }}
              aria-pressed={isFavorited}
              aria-label={isFavorited ? tr('navigation.favorites') : tr('converters.addToList')}
            >
              {favoriteDisabled ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Heart size={20} fill={isFavorited ? "currentColor" : "none"} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
